import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, User, Clock, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BookingDialog } from "@/components/BookingDialog";
import { getCourse } from "@/lib/api";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getCourse(slug).then(setCourse).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="ytca-container py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Course not found</h1>
      <Button asChild className="mt-6 rounded-full"><Link to="/learn-earn">Back to Learn & Earn</Link></Button>
    </div>
  );
  if (!course) return <div className="ytca-container py-24 text-muted-foreground">Loading…</div>;

  const isFree = course.kind === "free";

  return (
    <div>
      <section className="border-b border-border bg-secondary/40">
        <div className="ytca-container py-10">
          <Link to="/learn-earn" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="course-back-link"><ArrowLeft className="h-4 w-4" />Learn & Earn</Link>
          <div className="mt-6 grid gap-8 lg:grid-cols-[1.3fr_0.7fr] lg:items-center">
            <div>
              <Badge className={`rounded-full ${isFree ? "bg-accent text-accent-foreground" : ""}`}>{course.type_label}</Badge>
              <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">{course.title}</h1>
              <p className="mt-4 max-w-xl text-lg text-muted-foreground">{course.overview}</p>
              <div className="mt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-primary" />{course.instructor}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />{course.duration}</span>
                <span className="inline-flex items-center gap-1.5"><LayoutGrid className="h-4 w-4 text-primary" />{course.level}</span>
              </div>
            </div>
            <div className="overflow-hidden rounded-3xl border border-border">
              <img src={course.image} alt={course.title} className="aspect-[4/3] w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <div className="ytca-container py-12">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_0.6fr]">
          <div>
            <h2 className="font-display text-2xl font-semibold">What you'll learn</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {(course.learn || []).map((x, i) => (
                <li key={i} className="flex items-start gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-foreground/80">{x}</span></li>
              ))}
            </ul>

            <h2 className="mt-12 font-display text-2xl font-semibold">Curriculum</h2>
            <Accordion type="single" collapsible className="mt-4">
              {(course.curriculum || []).map((c, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left" data-testid={`curriculum-item-${i}`}>{c}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">Detailed lessons and exercises for this module.</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start">
            <Card className="rounded-2xl border-border bg-card p-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-3xl font-semibold" data-testid="course-price">{isFree ? "Free" : `₹${course.price}`}</span>
                <span className="text-xs text-muted-foreground">{course.format}</span>
              </div>
              <Button onClick={() => setOpen(true)} className="mt-6 w-full rounded-full" size="lg" data-testid="course-enroll-button">
                {isFree ? "Get Free Access" : "Enrol Now"}
              </Button>
              <p className="mt-3 text-center text-xs text-muted-foreground">{isFree ? "Instant access to the workbook." : "Secure payment via Razorpay."}</p>
            </Card>
          </div>
        </div>
      </div>

      <BookingDialog type="course" item={course} open={open} setOpen={setOpen} />
    </div>
  );
}
